import { localize } from "@/i18n/translate";
import { useRef, useState } from "react";
import { FileText, Loader2, Paperclip, Send, X } from "lucide-react";
import { api, downloadFile, errorCode } from "@/lib/api";
import { usePortal } from "./i18n";
import { ErrorNote, btnPrimary } from "./ui";
const EXT = ["pdf", "png", "jpg", "jpeg", "webp", "txt", "csv", "docx", "xlsx"];
const MAX = 10 * 1024 * 1024;
const kb = n => n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
export const checkFiles = files => {
  if (files.length > 5) return "too_many_files";
  for (const f of files) {
    const ext = f.name.split(".").pop().toLowerCase();
    if (!EXT.includes(ext)) return "file_type";
    if (f.size > MAX) return "file_size";
    if (!f.size) return "file_empty";
  }
  return null;
};
export const FilePicker = ({
  files,
  setFiles,
  onError,
  idPrefix
}) => {
  const {
    p
  } = usePortal();
  const ref = useRef(null);
  const add = e => {
    const next = [...files, ...Array.from(e.target.files || [])];
    e.target.value = "";
    const err = checkFiles(next);
    if (err) return onError(err);
    onError(null);
    setFiles(next);
  };
  return <div>
      <input ref={ref} id={`${idPrefix}-files`} type="file" multiple accept={EXT.map(x => `.${x}`).join(",")} onChange={add} className="sr-only" data-testid={`${idPrefix}-file-input`} />
      <label htmlFor={`${idPrefix}-files`} className="inline-flex cursor-pointer items-center gap-2 border border-ink/25 px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:border-ink focus-within:outline focus-within:outline-2 focus-within:outline-signal">
        <Paperclip className="h-4 w-4" aria-hidden="true" /> {localize(p.thread.attach)}
      </label>
      <p className="mt-2 text-xs text-mute">{localize(p.thread.fileRules)}</p>
      {localize(files.length > 0 && <ul className="mt-3 flex flex-wrap gap-2" data-testid={`${idPrefix}-file-list`}>
          {localize(files.map((f, i) => <li key={`${f.name}-${i}`} className="flex items-center gap-2 border border-ink/15 bg-white/70 py-1.5 pl-3 pr-1 text-sm">
              <FileText className="h-4 w-4 text-mute" aria-hidden="true" />
              <span className="max-w-[180px] truncate">{localize(f.name)}</span>
              <span className="font-mono text-xs text-mute">{localize(kb(f.size))}</span>
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="p-1.5 hover:text-signal" aria-label={localize(`${f.name} ${p.thread.remove}`)}>
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>))}
        </ul>)}
    </div>;
};
const Attachment = ({
  file
}) => {
  const {
    p,
    errText
  } = usePortal();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const get = async () => {
    setBusy(true);
    setErr(null);
    try {
      await downloadFile(file);
    } catch (e) {
      setErr(errText(errorCode(e)));
    } finally {
      setBusy(false);
    }
  };
  return <li>
      <button type="button" onClick={get} disabled={busy} data-testid={`attachment-${file.id}`} className="flex items-center gap-2 border border-ink/15 bg-white/70 px-3 py-2 text-left text-sm hover:border-ink" aria-label={localize(`${p.thread.download}: ${file.original_filename}`)}>
        {localize(busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <FileText className="h-4 w-4 text-signal" aria-hidden="true" />)}
        <span className="max-w-[220px] truncate">{localize(file.original_filename)}</span>
        <span className="font-mono text-xs text-mute">{localize(kb(file.size))}</span>
      </button>
      {localize(err && <p className="mt-1 text-xs text-signaldark">{localize(err)}</p>)}
    </li>;
};
export const Thread = ({
  messages,
  viewer
}) => {
  const {
    p,
    fmtDate
  } = usePortal();
  return <ol className="space-y-4" data-testid="message-thread">
      {localize(messages.map(m => {
      const staff = m.author_role === "admin";
      const name = staff ? viewer === "admin" ? m.author_name || p.thread.staff : p.thread.staff : viewer === "client" ? p.thread.you : m.author_name || p.thread.client;
      return <li key={m.id} data-testid={`message-${m.id}`} className={`border p-5 md:p-6 ${staff ? "border-ink/15 border-l-2 border-l-signal bg-white/80" : "border-ink/15 bg-paper2/60"}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-ink">{localize(name)}</p>
              <p className="font-mono text-xs text-mute">{localize(fmtDate(m.created_at))}</p>
            </div>
            {localize(m.body && <p className="mt-3 whitespace-pre-wrap break-words leading-relaxed text-ink">{localize(m.body)}</p>)}
            {localize(m.attachments?.length > 0 && <ul className="mt-4 flex flex-wrap gap-2">
                {localize(m.attachments.map(f => <Attachment key={f.id} file={f} />))}
              </ul>)}
          </li>;
    }))}
    </ol>;
};
export const Composer = ({
  endpoint,
  onSent
}) => {
  const {
    p,
    errText
  } = usePortal();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault();
    if (!body.trim() && !files.length) return setErr(p.thread.emptyMsg);
    setBusy(true);
    setErr(null);
    const fd = new FormData();
    fd.append("body", body);
    files.forEach(f => fd.append("files", f));
    try {
      await api.post(endpoint, fd);
      setBody("");
      setFiles([]);
      onSent?.();
    } catch (ex) {
      setErr(errText(errorCode(ex)));
    } finally {
      setBusy(false);
    }
  };
  return <form onSubmit={submit} className="space-y-4 border border-ink/15 bg-white/60 p-5 md:p-6" data-testid="message-composer" noValidate>
      <label htmlFor="reply-body" className="text-sm font-medium text-ink">{localize(p.thread.reply)}</label>
      <textarea id="reply-body" rows={4} value={body} onChange={e => setBody(e.target.value)} placeholder={localize(p.thread.placeholder)} data-testid="composer-body" className="block w-full resize-y border border-ink/25 bg-white px-4 py-3 text-ink placeholder:text-zinc-500 focus:border-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-signal" />
      <FilePicker files={files} setFiles={setFiles} onError={c => setErr(c ? errText(c) : null)} idPrefix="composer" />
      <ErrorNote testId="composer-error">{localize(err)}</ErrorNote>
      <div className="flex justify-end">
        <button type="submit" disabled={busy} className={btnPrimary} data-testid="composer-send">
          {localize(busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />)}
          {localize(busy ? p.thread.sending : p.thread.send)}
        </button>
      </div>
    </form>;
};
